import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { DialogOpenEvent } from '@theme/dialog';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Keeps the add-ons on the selected variant and runs their cards as an accordion. A variant change re-renders
 * only the variant picker, so the add-ons are taken from the section it fetched; that also clears the selection.
 * Opening a card closes the others unless they hold a checked add-on.
 * The details dialog adds and removes add-ons by ticking the page checkboxes, which stay the source of truth.
 * An out-of-stock variant keeps its add-ons on show but inert.
 * Under the free add-on promotion, eligible add-ons show their discounted price while discounts are left; once
 * they are taken, only the first add-ons ticked keep it.
 *
 * @extends {Component}
 */
class ProductAddOns extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string[]} Variant ids of the ticked promotion add-ons, in the order they were ticked. */
  #freeAddOnOrder = [];

  /** @type {string[]} Variant ids of the ticked add-ons the promotion covers. */
  #coveredFreeAddOns = [];

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const container = this.closest('.shopify-section, dialog');
    container?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    // `toggle` does not bubble
    this.addEventListener('toggle', this.#handleToggle, { signal, capture: true });
    this.addEventListener('change', this.#handleChange, { signal });
    // The dialog's open event does not bubble either
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, capture: true });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    this.#updateFreeAddOns();
    this.#syncToggles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {HTMLDetailsElement[]} */
  get #cards() {
    return Array.from(this.querySelectorAll('details.product-add-ons__card'));
  }

  /**
   * Ticks or unticks the page checkbox for the add-on a dialog button stands for.
   * @param {MouseEvent} event - The click event.
   */
  toggleAddOn(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-add-on-variant-id]');
    if (!(button instanceof HTMLElement)) return;

    const input = this.querySelector(
      `.product-add-ons__list [data-add-on-input][value="${button.dataset.addOnVariantId}"]`
    );
    if (!(input instanceof HTMLInputElement)) return;

    input.checked = !input.checked;
    // Announced before the change, which may add the free add-on announcement to it
    this.#announce(input.checked ? button.dataset.addedAnnouncement : button.dataset.removedAnnouncement);
    input.dispatchEvent(new Event('change', { bubbles: true }));

    const card = input.closest('details');
    if (input.checked && card && !card.open) card.open = true;
  }

  /**
   * @param {Event} event - The change event.
   */
  #handleChange = (event) => {
    this.#updateFreeAddOns(event.target);
    this.#syncToggles();
  };

  /**
   * Marks the promotion add-ons that show the discount: the first ones ticked, up to the promotion's quantity, and
   * every unticked one while discounts are left, since ticking it would get one.
   * @param {EventTarget | null} [changedInput] - The checkbox just changed; without it nothing is announced.
   */
  #updateFreeAddOns(changedInput = null) {
    const quantity = Number(this.dataset.freeAddOnQuantity) || 0;
    const tiles = Array.from(this.querySelectorAll('.product-add-ons__list [data-free-add-on]'));
    if (quantity === 0 || tiles.length === 0) return;

    const checkedValues = tiles
      .map((tile) => tile.querySelector('[data-add-on-input]'))
      .filter((input) => input instanceof HTMLInputElement && input.checked)
      .map((input) => /** @type {HTMLInputElement} */ (input).value);

    this.#freeAddOnOrder = this.#freeAddOnOrder.filter((value) => checkedValues.includes(value));
    for (const value of checkedValues) {
      if (!this.#freeAddOnOrder.includes(value)) this.#freeAddOnOrder.push(value);
    }

    const coveredValues = this.#freeAddOnOrder.slice(0, quantity);
    const hasDiscountsLeft = coveredValues.length < quantity;
    const announcements = [];

    for (const tile of tiles) {
      if (!(tile instanceof HTMLElement)) continue;

      const input = tile.querySelector('[data-add-on-input]');
      if (!(input instanceof HTMLInputElement)) continue;

      const isCovered = coveredValues.includes(input.value);
      const isNewlyCovered = isCovered && !this.#coveredFreeAddOns.includes(input.value);

      if (changedInput && isNewlyCovered && tile.dataset.freeAnnouncement) {
        announcements.push(tile.dataset.freeAnnouncement);
      }
      tile.toggleAttribute('data-discounted', isCovered || (!input.checked && hasDiscountsLeft));
    }

    this.#coveredFreeAddOns = coveredValues;
    if (announcements.length > 0) this.#announceFreeAddOns(announcements.join(' '));
  }

  /**
   * Announces add-ons that just became free: after the dialog's own announcement while it is open, on the page
   * otherwise, since a modal dialog hides the page from assistive technology.
   * @param {string} message - The text to announce.
   */
  #announceFreeAddOns(message) {
    const dialogRegion = this.querySelector('dialog[open] [data-announcement-region]');
    if (dialogRegion) {
      dialogRegion.textContent = `${dialogRegion.textContent} ${message}`.trim();
      return;
    }

    const region = this.querySelector('[data-free-add-on-region]');
    if (region) region.textContent = message;
  }

  /**
   * Matches every dialog button to its page checkbox, discounted price included.
   */
  #syncToggles = () => {
    for (const button of this.querySelectorAll('.product-add-ons__toggle')) {
      if (!(button instanceof HTMLElement)) continue;

      const input = this.querySelector(
        `.product-add-ons__list [data-add-on-input][value="${button.dataset.addOnVariantId}"]`
      );
      const isAdded = input instanceof HTMLInputElement && input.checked;
      const isDiscounted = Boolean(input?.closest('[data-discounted]'));

      button.toggleAttribute('data-added', isAdded);
      if (button.hasAttribute('data-free-add-on')) button.toggleAttribute('data-discounted', isDiscounted);
    }
  };

  /**
   * Opens the dialog on its first add-on, with every button matching the page.
   */
  #handleDialogOpen = () => {
    this.#syncToggles();
    this.#announce('');

    const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
      this.querySelector('slideshow-component')
    );
    slideshow?.select(0, undefined, { animate: false });
  };

  /**
   * Announces the add-on a shopper moved to.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (!event.detail.userInitiated) return;

    const announcement = event.detail.slide.querySelector('[data-announcement]');
    if (!(announcement instanceof HTMLElement)) return;

    this.#announce(announcement.dataset.announcement);
  };

  /**
   * Writes to the dialog's live region.
   * @param {string | undefined} message - The text to announce.
   */
  #announce(message) {
    const region = this.querySelector('[data-announcement-region]');
    if (region) region.textContent = message ?? '';
  }

  /**
   * Closes the other cards when one opens, sparing those with a checked add-on.
   * @param {Event} event - The toggle event.
   */
  #handleToggle = (event) => {
    const openedCard = event.target;
    if (!(openedCard instanceof HTMLDetailsElement) || !openedCard.open) return;

    for (const card of this.#cards) {
      if (card === openedCard || !card.open || card.querySelector(':checked')) continue;

      card.open = false;
    }
  };

  /**
   * Re-renders the add-ons for the selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        // A morphed element, such as `<main>` for a combined listing sibling, already carries the new add-ons.
        if (!detail.morphElementSelector) {
          const newAddOns = detail.html.querySelector(`product-add-ons[data-block-id="${this.dataset.blockId}"]`);
          if (!newAddOns) return;

          morph(this, newAddOns, MORPH_OPTIONS);
          // The morph leaves the element's own attributes alone
          this.hidden = newAddOns.hasAttribute('hidden');
          this.inert = newAddOns.hasAttribute('inert');
          const freeAddOnQuantity = newAddOns.getAttribute('data-free-add-on-quantity');
          if (freeAddOnQuantity) {
            this.setAttribute('data-free-add-on-quantity', freeAddOnQuantity);
          } else {
            this.removeAttribute('data-free-add-on-quantity');
          }
        }

        // The re-render resets the selection
        this.#freeAddOnOrder = [];
        this.#coveredFreeAddOns = [];
        this.#updateFreeAddOns();
        this.#syncToggles();
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[product-add-ons] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-add-ons')) {
  customElements.define('product-add-ons', ProductAddOns);
}
